/**
 * UserManagement.js - Admin User Management
 * 
 * Allows admins to:
 * - View all users
 * - Create new users (with role selection)
 * - Delete users
 * - Reset passwords
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

function UserManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', username: '', password: '', role: 'USER' });
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Load users
  const loadUsers = () => {
    setUsers(dataStore.getUsers());
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Create new user
  const handleCreate = async () => {
    setError('');
    
    // Validation
    if (!newUser.name.trim() || !newUser.username.trim() || !newUser.password.trim()) {
      setError('All fields are required');
      return;
    }
    
    try {
      await dataStore.createUser(newUser, currentUser);
      setNewUser({ name: '', username: '', password: '', role: 'USER' });
      setShowCreateForm(false);
      loadUsers();
      setMessage('User created successfully!');
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    // Prevent self-deletion
    if (userId === currentUser?.id) {
      setError('You cannot delete your own account!');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user? This will also remove them from all schedules.')) {
      try {
        await dataStore.deleteUser(userId, currentUser);
        loadUsers();
        setMessage('User deleted successfully');
      } catch (err) {
        console.error('Error deleting user:', err);
        setError(err.message);
      }
    }
  };

  // Reset password
  const handleResetPassword = async (userId) => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }
    
    try {
      await dataStore.resetUserPassword(userId, newPassword, currentUser);
      setResetPasswordId(null);
      setNewPassword('');
      setMessage('Password reset successfully!');
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
    }
  };

  // Toggle canViewAllStudents permission
  const handleToggleViewAllStudents = async (userId, currentValue) => {
    try {
      await dataStore.updateUser(userId, { canViewAllStudents: !currentValue }, currentUser);
      loadUsers();
      const user = users.find(u => u.id === userId);
      setMessage(`"Can View All Students" ${!currentValue ? 'granted to' : 'revoked from'} ${user?.name}`);
    } catch (err) {
      console.error('Error updating permission:', err);
      setError(err.message);
    }
  };

  // Change user role
  const handleRoleChange = async (userId, newRole) => {
    // Prevent changing own role
    if (userId === currentUser?.id) {
      setError('You cannot change your own role!');
      return;
    }
    
    try {
      await dataStore.updateUser(userId, { role: newRole }, currentUser);
      loadUsers();
      const user = users.find(u => u.id === userId);
      const roleLabel = newRole === 'SUPER_ADMIN' ? 'Super Admin' : newRole === 'ADMIN' ? 'Admin' : 'Teacher';
      setMessage(`${user?.name}'s role changed to ${roleLabel}`);
    } catch (err) {
      console.error('Error changing role:', err);
      setError(err.message);
    }
  };

  // =============================================
  // STYLES
  // =============================================
  const containerStyle = {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '25px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    marginBottom: '20px'
  };

  const titleStyle = {
    color: theme.text,
    marginTop: 0
  };

  const buttonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: darkMode ? '#fff' : '#333',
    marginRight: '10px',
    fontSize: '0.9rem'
  };

  const inputStyle = {
    padding: '10px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.background,
    color: theme.text,
    marginRight: '10px',
    marginBottom: '10px',
    width: '180px'
  };

  const selectStyle = {
    ...inputStyle,
    width: '120px',
    cursor: 'pointer'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    padding: '12px',
    backgroundColor: colors.green,
    color: darkMode ? '#fff' : '#333',
    textAlign: 'left',
    fontWeight: '600'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: `1px solid ${theme.border}`,
    color: theme.text
  };

  const roleTagStyle = (role) => ({
    padding: '4px 12px',
    borderRadius: '12px',
    backgroundColor: role === 'SUPER_ADMIN' ? colors.purple || '#9c27b0' : role === 'ADMIN' ? colors.red : colors.blue,
    color: darkMode ? '#fff' : '#333',
    fontSize: '0.85rem',
    fontWeight: 'bold'
  });

  const messageStyle = {
    padding: '12px 20px',
    borderRadius: '8px',
    textAlign: 'center',
    position: 'fixed',
    top: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    minWidth: '300px',
    fontWeight: '600'
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={titleStyle}>👥 User Management</h1>
          <button
            style={{ ...buttonStyle, backgroundColor: colors.green, color: darkMode ? '#fff' : '#333', fontWeight: '600' }}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ Add User'}
          </button>
        </div>

        {/* Messages */}
        {message && (
          <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#fff' : '#333', fontWeight: '600' }}>
            {message}
          </div>
        )}
        {error && (
          <div style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#333' }}>
            {error}
          </div>
        )}

        {/* Create User Form */}
        {showCreateForm && (
          <div style={{ 
            marginTop: '20px', 
            marginBottom: '20px', 
            padding: '20px', 
            backgroundColor: theme.backgroundSecondary, 
            borderRadius: '8px' 
          }}>
            <h3 style={{ color: theme.text, marginTop: 0 }}>Create New User</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                style={inputStyle}
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                style={selectStyle}
              >
                <option value="USER">Teacher</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
              </select>
              <button
                style={{ ...buttonStyle, backgroundColor: colors.blue, marginBottom: '10px' }}
                onClick={handleCreate}
              >
                Create User
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Username</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>Permissions</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td style={tdStyle}>
                  <strong>{user.name}</strong>
                  {user.id === currentUser?.id && (
                    <span style={{ marginLeft: '8px', color: theme.textSecondary, fontSize: '0.8rem' }}>
                      (you)
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{user.username}</td>
                <td style={tdStyle}>
                  {user.id === currentUser?.id ? (
                    <span style={roleTagStyle(user.role)}>
                      {user.role === 'SUPER_ADMIN' ? 'Super Admin' : user.role === 'ADMIN' ? 'Admin' : 'Teacher'}
                    </span>
                  ) : (
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: theme.backgroundSecondary,
                        color: theme.text,
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="USER">Teacher</option>
                      <option value="ADMIN">Admin</option>
                      <option value="SUPER_ADMIN">Super Admin</option>
                    </select>
                  )}
                </td>
                <td style={tdStyle}>
                  {user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN' && (
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: theme.text
                    }}>
                      <input
                        type="checkbox"
                        checked={user.canViewAllStudents || false}
                        onChange={() => handleToggleViewAllStudents(user.id, user.canViewAllStudents)}
                        style={{ 
                          width: '16px', 
                          height: '16px', 
                          cursor: 'pointer',
                          accentColor: colors.green
                        }}
                      />
                      View All Students
                    </label>
                  )}
                  {(user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') && (
                    <span style={{ color: theme.textSecondary, fontSize: '0.85rem', fontStyle: 'italic' }}>
                      Full access
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {/* Password Reset */}
                  {resetPasswordId === user.id ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                      <input
                        type="password"
                        placeholder="New password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        style={{ ...inputStyle, width: '120px', marginBottom: 0, marginRight: 0 }}
                      />
                      <button
                        style={{ ...buttonStyle, backgroundColor: colors.green, padding: '6px 12px', marginRight: '5px', color: darkMode ? '#fff' : '#333', fontWeight: '600' }}
                        onClick={() => handleResetPassword(user.id)}
                      >
                        Save
                      </button>
                      <button
                        style={{ ...buttonStyle, backgroundColor: theme.textSecondary, padding: '6px 12px' }}
                        onClick={() => { setResetPasswordId(null); setNewPassword(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        style={{ ...buttonStyle, backgroundColor: colors.blue, padding: '6px 12px' }}
                        onClick={() => setResetPasswordId(user.id)}
                      >
                        Reset PW
                      </button>
                      <button
                        style={{ 
                          ...buttonStyle, 
                          backgroundColor: user.id === currentUser?.id ? theme.textSecondary : colors.red, 
                          color: '#fff',
                          padding: '6px 12px',
                          cursor: user.id === currentUser?.id ? 'not-allowed' : 'pointer'
                        }}
                        onClick={() => handleDelete(user.id)}
                        disabled={user.id === currentUser?.id}
                        title={user.id === currentUser?.id ? "You can't delete yourself" : "Delete user"}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <p style={{ textAlign: 'center', color: theme.textSecondary, padding: '20px' }}>
            No users found. Click "Add User" to create one.
          </p>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
